import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  createSaEmailSuppression,
  deleteSaEmailSuppression,
  getSaEmailSuppressions,
  SaEmailSuppression,
} from "../../../helpers/super_admin_ops_helper";

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Suppression global de e-mails (LAC-036 · DOC 02 § 6.3).
 */
const SuperAdminEmailSuppressions = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";

  const [items, setItems] = useState<SaEmailSuppression[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SaEmailSuppression | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  document.title = t("sa.suppressions.email.documentTitle");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaEmailSuppressions({
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
      });
      if (!res?.success) {
        setError(t("sa.suppressions.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("sa.suppressions.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const openCreate = () => {
    setEmail("");
    setReason("");
    setFieldError(null);
    setModalOpen(true);
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFieldError(null);
    try {
      const res = await createSaEmailSuppression({
        email: email.trim(),
        reason: reason.trim() || undefined,
      });
      toast.success(res?.message || t("sa.suppressions.toast.emailCreated"), {
        autoClose: 2500,
      });
      setModalOpen(false);
      await load();
    } catch (err: any) {
      const details = err?.details?.email;
      setFieldError(
        Array.isArray(details)
          ? details[0]
          : err?.message || t("sa.suppressions.error.save")
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    try {
      const res = await deleteSaEmailSuppression(deleting.id);
      toast.success(res?.message || t("sa.suppressions.toast.emailDeleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      toast.error(err?.message || t("sa.suppressions.error.delete"), {
        autoClose: 4000,
      });
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.suppressions.email.title")}
          pageTitle={t("sa.menu.reputation")}
        />
        <ToastContainer closeButton={false} limit={2} />

        {error && (
          <Alert color="danger" className="d-flex justify-content-between gap-2">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={() => load()}>
              {t("sa.suppressions.retry")}
            </Button>
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 flex-grow-1">
                  <Input
                    type="search"
                    bsSize="sm"
                    style={{ maxWidth: 260 }}
                    placeholder={t("sa.suppressions.searchEmail")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label={t("sa.suppressions.searchEmail")}
                  />
                </div>
                <Button color="primary" size="sm" onClick={openCreate}>
                  {t("sa.suppressions.add")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {t("sa.suppressions.email.empty")}
                    </p>
                    <Button color="primary" onClick={openCreate}>
                      {t("sa.suppressions.add")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      {t("sa.suppressions.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("sa.suppressions.table.email")}</th>
                            <th>{t("sa.suppressions.table.reason")}</th>
                            <th>{t("sa.suppressions.table.source")}</th>
                            <th>{t("sa.suppressions.table.since")}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((row) => (
                            <tr key={row.id}>
                              <td>{row.email}</td>
                              <td>{row.reason || "—"}</td>
                              <td>{row.source || "—"}</td>
                              <td className="small text-muted">
                                {row.suppressed_at
                                  ? new Date(row.suppressed_at).toLocaleString(
                                      locale
                                    )
                                  : "—"}
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  onClick={() => setDeleting(row)}
                                >
                                  {t("sa.suppressions.remove")}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between mt-3">
                        <Button
                          color="light"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {t("sa.suppressions.prev")}
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          disabled={page >= lastPage}
                          onClick={() =>
                            setPage((p) => Math.min(lastPage, p + 1))
                          }
                        >
                          {t("sa.suppressions.next")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => !saving && setModalOpen(false)} centered>
        <Form onSubmit={submitCreate}>
          <ModalHeader toggle={() => !saving && setModalOpen(false)}>
            {t("sa.suppressions.email.addTitle")}
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label for="sa-email-sup">{t("sa.suppressions.table.email")}</Label>
              <Input
                id="sa-email-sup"
                type="email"
                value={email}
                invalid={Boolean(fieldError)}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {fieldError ? <FormFeedback>{fieldError}</FormFeedback> : null}
            </div>
            <div className="mb-0">
              <Label for="sa-email-reason">
                {t("sa.suppressions.table.reason")}
              </Label>
              <Input
                id="sa-email-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={64}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" disabled={saving} onClick={() => setModalOpen(false)}>
              {t("sa.suppressions.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : t("sa.suppressions.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("sa.suppressions.email.deleteTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {t("sa.suppressions.email.deleteConfirm", {
              email: deleting?.email || "",
            })}
          </p>
          <p className="text-muted small mb-0 mt-2">
            {t("sa.suppressions.deleteConsequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("sa.suppressions.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={() => void confirmDelete()}
          >
            {deletingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("sa.suppressions.removeConfirm")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SuperAdminEmailSuppressions;
